// Czech labels for the mail outbox (docs/maily.md). The kind keys are Go's
// `mail.Kind` values; TestEveryOutboxKindHasAnAdminLabel
// (internal/mail/labels_test.go) fails when one here is missing, because a
// kind with no label would show its raw machine key on /admin/maily.

import { pluralizeCzech } from "@marekl/adminer/format";
import type {
  MailDeadline,
  MailOutboxStatus,
  MailReminderRules,
  MailSwitch,
  MailTiming,
} from "../../../lib/queries/mailOutbox";

type BadgeVariant = "ok" | "warn" | "err" | "info" | "muted";

export const MAIL_KIND_LABELS: Record<string, string> = {
  team_pending_approval: "Tým čeká na schválení",
  team_approved: "Tým byl schválen",
  team_leader_assigned: "Vedení týmu předáno",
  season_registered: "Registrace do sezóny přijata",
  season_unregistered: "Registrace do sezóny zrušena",
  entry_confirmed: "Přihláška přijata",
  entry_withdrawn: "Odhláška",
  entry_withdrawn_organizer: "Odhláška (organizátorovi)",
  entry_reinstated: "Přihláška vrácena zpět",
  entry_music_warning: "Problém s hudbou",
  competition_published: "Soutěž zveřejněna",
  startlist_published: "Startovní listina zveřejněna",
  startlist_published_organizer: "Startovní listina zveřejněna (organizátorovi)",
  start_scratched: "Start přeškrtnut",
  start_restored: "Přeškrtnutí zrušeno",
  season_opened: "Registrace do sezóny otevřena",
  registration_closing: "Registrace do sezóny se uzavírá",
  missing_photos: "Chybí fotky členů",
  documents_missing: "Neodsouhlasené dokumenty sezóny",
  entry_changes_closing: "Blíží se uzávěrka",
  music_missing: "Chybí hudba",
  no_entries: "Soutěž nemá přihlášky",
  competition_draft: "Soutěž se stále připravuje",
  // Sent straight from the request and only recorded here (docs/maily.md R16).
  password_reset: "Obnova hesla",
  account_created: "Účet byl vytvořen",
  password_set_by_admin: "Heslo bylo obnoveno",
};

export function mailKindLabel(kind: string): string {
  return MAIL_KIND_LABELS[kind] ?? kind;
}

export const MAIL_STATUS_LABELS: Record<MailOutboxStatus, string> = {
  pending: "Čeká",
  sent: "Odesláno",
  cancelled: "Zrušeno",
  expired: "Propadlo",
  failed: "Selhalo",
  unreachable: "Bez adresáta",
  disabled: "Vypnuto",
};

export const MAIL_STATUS_VARIANT: Record<MailOutboxStatus, BadgeVariant> = {
  pending: "info",
  sent: "ok",
  cancelled: "muted",
  // Never left, and never will: somebody was not told something.
  expired: "warn",
  failed: "err",
  unreachable: "warn",
  // An administrator chose this; nothing to chase.
  disabled: "muted",
};

/** Section headings of the settings tab, keyed by `mail.Section.Key`. */
export const MAIL_SECTION_LABELS: Record<string, string> = {
  accounts: "Účty a přístup",
  club: "Registrace a život týmu",
  season: "Sezóna a soupiska",
  competition: "Soutěže, přihlášky, hudba",
};

/**
 * A switch holding a pair of kinds is named for the pair, keyed by its first
 * kind. A one-kind switch takes the kind's own label.
 */
const MAIL_PAIR_LABELS: Record<string, string> = {
  entry_withdrawn: "Odhláška",
  startlist_published: "Startovní listina zveřejněna",
  start_scratched: "Přeškrtnutí startu a jeho zrušení",
};

export function mailSwitchLabel(kinds: string[]): string {
  return MAIL_PAIR_LABELS[kinds[0]] ?? mailKindLabel(kinds[0]);
}

/** Who gets the mail, under each switch, keyed by its first kind. */
const MAIL_SWITCH_RECIPIENTS: Record<string, string> = {
  password_reset: "Uživateli, který požádá o nové heslo.",
  account_created: "Novému uživateli, s odkazem na nastavení hesla.",
  password_set_by_admin: "Uživateli, kterému administrátor obnovil heslo.",
  team_pending_approval: "Všem, kdo smějí nový tým schválit.",
  team_approved: "Vedoucímu schváleného týmu.",
  team_leader_assigned: "Novému vedoucímu týmu.",
  season_registered: "Vedoucímu týmu, který se zaregistroval do sezóny.",
  season_unregistered: "Vedoucímu týmu, jehož registrace do sezóny byla zrušena.",
  season_opened: "Vedoucím všech týmů ČNSMS.",
  registration_closing: "Vedoucím týmů, které už členy ČNSMS byly a do sezóny se ještě nezaregistrovaly.",
  missing_photos: "Vedoucímu týmu, jehož členům chybí foto.",
  documents_missing: "Vedoucímu zaregistrovaného týmu, který neodsouhlasil některý dokument sezóny.",
  competition_published: "Vedoucím týmů, které se na soutěž mohou přihlásit.",
  entry_confirmed: "Vedoucímu týmu, když je přihláška podána nebo změněna.",
  entry_withdrawn: "Týmu i organizátorům soutěže.",
  entry_reinstated: "Týmu, jehož odhlášená přihláška se vrátila zpět.",
  entry_changes_closing: "Týmům s přihláškou na soutěž.",
  music_missing: "Týmům, kterým k přihlášce chybí vlastní hudba.",
  entry_music_warning: "Týmu, jehož nahraná hudba je delší nebo kratší než limit, nebo má špatnou kvalitu.",
  startlist_published: "Týmům na listině i organizátorům.",
  start_scratched: "Týmu, kterému byl start po zveřejnění listiny přeškrtnut nebo vrácen.",
  no_entries: "Organizátorům soutěže, která nemá žádnou přihlášku.",
  competition_draft: "Organizátorům soutěže, která se stále připravuje.",
};

/**
 * What a delay counts from, keyed by the switch's first kind: „Hned …" or
 * „5 minut …" goes in front. No numbers here; they come from the server
 * (`MailTiming`).
 */
const MAIL_SWITCH_TRIGGERS: Record<string, string> = {
  password_reset: "po žádosti",
  account_created: "při založení účtu",
  password_set_by_admin: "po obnovení hesla",
  team_pending_approval: "po odeslání žádosti o registraci týmu",
  team_approved: "po schválení",
  team_leader_assigned: "po předání; předání, které se do té doby předá dál, se neoznamuje",
  season_registered: "po registraci; registrace do té doby zrušená se neoznamuje",
  season_unregistered: "po zrušení registrace",
  competition_published: "po zveřejnění soutěže",
  entry_confirmed: "po poslední změně; několik úprav za sebou je jeden e-mail",
  entry_withdrawn: "po odhlášení; odhláška do té doby vrácená zpět se neoznamuje",
  entry_reinstated: "po vrácení zpět",
  entry_music_warning: "po nahrání hudby; novější soubor varování o předchozím zruší",
  startlist_published: "po zveřejnění listiny",
  start_scratched: "po přeškrtnutí nebo jeho zrušení",
};

/** A reminder's deadline, in the genitive after „před". */
const MAIL_DEADLINE_LABELS: Record<MailDeadline, string> = {
  registration_opens: "otevřením registračního okna",
  registration_closes: "koncem registračního okna",
  entry_deadline: "uzávěrkou přihlášek",
  roster_deadline: "uzávěrkou změn soutěžících",
  music_deadline: "uzávěrkou hudby",
  competition_date: "termínem soutěže",
};

const DAYS: [string, string, string] = ["den", "dny", "dní"];

function delaySentence(kind: string, minutes: number): string | undefined {
  const trigger = MAIL_SWITCH_TRIGGERS[kind];
  if (!trigger) return undefined;
  if (minutes === 0) return `Hned ${trigger}.`;
  if (minutes === 1) return `Minutu ${trigger}.`;
  return `${minutes} ${pluralizeCzech(minutes, ["minutu", "minuty", "minut"])} ${trigger}.`;
}

function reminderSentence(t: MailTiming): string | undefined {
  if (!t.deadline) return undefined;
  if (t.deadline === "registration_opens") {
    const n = t.days_after ?? 0;
    return `V den otevření registračního okna, nejpozději do ${n} ${n === 1 ? "dne" : "dnů"} po něm.`;
  }
  const days = t.days_before ?? [];
  if (days.length === 0) return undefined;
  const list = days.length === 1 ? String(days[0]) : `${days.slice(0, -1).join(", ")} a ${days[days.length - 1]}`;
  const noun = pluralizeCzech(days[days.length - 1], DAYS);
  const head = days.length === 1 ? "Upomínka" : "Upomínky";
  let out = `${head} ${list} ${noun} před ${MAIL_DEADLINE_LABELS[t.deadline]}`;
  if (t.published_for_days) {
    out += `, je-li soutěž zveřejněná aspoň ${t.published_for_days} ${pluralizeCzech(t.published_for_days, DAYS)}`;
  }
  return `${out}.`;
}

/** The hint under a switch: who gets the mail, then when it leaves. */
export function mailSwitchHint(sw: Pick<MailSwitch, "kinds" | "timing">): string | undefined {
  const when = sw.timing.deadline
    ? reminderSentence(sw.timing)
    : delaySentence(sw.kinds[0], sw.timing.delay_minutes);
  const parts = [MAIL_SWITCH_RECIPIENTS[sw.kinds[0]], when].filter(Boolean);
  return parts.length > 0 ? parts.join(" ") : undefined;
}

/** The rules every reminder follows, as one paragraph. */
export function reminderRulesSentence(r: MailReminderRules): string {
  const grace = r.grace_days === 1 ? "První den" : `V prvních ${r.grace_days} dnech`;
  return (
    `Upomínky odcházejí jen mezi ${r.from_hour}. a ${r.until_hour}. hodinou a každý dostane nejvýš jednu ` +
    `denně; další počká na příští den. ${grace} po zveřejnění soutěže nebo otevření registrace se ` +
    "nepřipomíná nic. Upomínka, kterou nejde odeslat týž den, propadne, a co se mezitím napravilo, se nepřipomíná."
  );
}
